import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ARGUS } from '../data/argusData';

function formatDA(n) {
  return n.toLocaleString('fr-FR').replace(/ /g, ' ') + ' DA';
}

function formatNum(n) {
  return Math.round(n).toLocaleString('fr-FR').replace(/ /g, ' ');
}

const AGE_OPTIONS = [
  { label: 'Neuve', key: 'neuve' },
  { label: '< 1 an', key: 'm1' },
  { label: '< 2 ans', key: 'm2' },
  { label: '< 3 ans', key: 'm3' },
];

export default function SimulateurDedouanement() {
  const [marque, setMarque] = useState('');
  const [modele, setModele] = useState('');
  const [motorisation, setMotorisation] = useState('');
  const [age, setAge] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const marques = useMemo(() => {
    const s = new Set(ARGUS.map(r => r.marque));
    return [...s].sort();
  }, []);

  const modeles = useMemo(() => {
    if (!marque) return [];
    const s = new Set(ARGUS.filter(r => r.marque === marque).map(r => r.modele));
    return [...s].sort();
  }, [marque]);

  const motorisations = useMemo(() => {
    if (!marque || !modele) return [];
    const rows = ARGUS.filter(r => r.marque === marque && r.modele === modele);
    const seen = new Set();
    return rows
      .map(r => ({ key: r.cyl + '|' + r.energie, label: r.cyl + ' - ' + r.energie }))
      .filter(o => { if (seen.has(o.key)) return false; seen.add(o.key); return true; })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [marque, modele]);

  const handleMarqueChange = (e) => {
    setMarque(e.target.value);
    setModele('');
    setMotorisation('');
    setResult(null);
    setError('');
  };

  const handleModeleChange = (e) => {
    setModele(e.target.value);
    setMotorisation('');
    setResult(null);
    setError('');
  };

  const handleMotorisationChange = (e) => {
    setMotorisation(e.target.value);
    setResult(null);
    setError('');
  };

  const handleAgeChange = (key) => {
    setAge(key);
    setResult(null);
    setError('');
  };

  const handleEstimer = () => {
    if (!marque || !modele || !motorisation || !age) {
      setError('Veuillez compléter toutes les étapes.');
      return;
    }
    const [cyl, energie] = motorisation.split('|');
    const row = ARGUS.find(
      r => r.marque === marque && r.modele === modele && r.cyl === cyl && r.energie === energie
    );
    if (!row) {
      setError('Aucune donnée trouvée pour cette combinaison.');
      return;
    }
    setError('');
    setResult({ row, ageKey: age });
  };

  const ageLabel = age ? AGE_OPTIONS.find(o => o.key === age)?.label : '';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff', fontFamily: 'sans-serif' }}>
      <Header />
      <main style={{ flex: 1, padding: '32px 16px', maxWidth: 720, margin: '0 auto', width: '100%' }}>

        {/* Titre */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#111', marginBottom: 6 }}>
            Simulateur de Dédouanement
          </h1>
          <p style={{ color: '#555', fontSize: '1rem' }}>
            Estimez le coût de dédouanement de votre véhicule en Algérie selon le barème DGD 2026.
          </p>
        </div>

        {/* Bandeau avertissement */}
        <div style={{
          background: '#FEFCE8', border: '1px solid #EAB308',
          borderRadius: 10, padding: '12px 18px', marginBottom: 20,
          color: '#713F12', fontSize: '0.875rem'
        }}>
          <strong>Avertissement :</strong> Ces estimations sont basées sur le barème officiel DGD Algérie 2026 et sont données à titre indicatif. Les montants réels peuvent varier selon la date de mise en circulation exacte et d'autres facteurs douaniers.
        </div>

        {/* Info taux */}
        <div style={{
          background: '#F9FAFB', border: '1px solid #E5E7EB',
          borderRadius: 10, padding: '10px 18px', marginBottom: 28,
          color: '#374151', fontSize: '0.875rem', textAlign: 'center'
        }}>
          <strong>Taux de référence :</strong> 1 EUR = 156 DZD &nbsp;|&nbsp; 1 USD = 133 DZD &nbsp;(Février 2026)
        </div>

        {/* Étape 1 — Marque */}
        <div style={stepCard}>
          <div style={stepHeader}>
            <span style={stepBadge}>1</span>
            <span style={stepTitle}>Sélectionnez la marque</span>
          </div>
          <select value={marque} onChange={handleMarqueChange} style={selectStyle}>
            <option value="">-- Choisir une marque --</option>
            {marques.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Étape 2 — Modèle */}
        <div style={{ ...stepCard, opacity: marque ? 1 : 0.5, pointerEvents: marque ? 'auto' : 'none' }}>
          <div style={stepHeader}>
            <span style={stepBadge}>2</span>
            <span style={stepTitle}>Sélectionnez le modèle</span>
          </div>
          <select value={modele} onChange={handleModeleChange} style={selectStyle} disabled={!marque}>
            <option value="">-- Choisir un modèle --</option>
            {modeles.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Étape 3 — Motorisation */}
        <div style={{ ...stepCard, opacity: modele ? 1 : 0.5, pointerEvents: modele ? 'auto' : 'none' }}>
          <div style={stepHeader}>
            <span style={stepBadge}>3</span>
            <span style={stepTitle}>Cylindrée &amp; Énergie</span>
          </div>
          <select value={motorisation} onChange={handleMotorisationChange} style={selectStyle} disabled={!modele}>
            <option value="">-- Choisir une motorisation --</option>
            {motorisations.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        {/* Étape 4 — Âge */}
        <div style={{ ...stepCard, opacity: motorisation ? 1 : 0.5, pointerEvents: motorisation ? 'auto' : 'none' }}>
          <div style={stepHeader}>
            <span style={stepBadge}>4</span>
            <span style={stepTitle}>État / Âge du véhicule</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {AGE_OPTIONS.map(o => (
              <button
                key={o.key}
                onClick={() => handleAgeChange(o.key)}
                disabled={!motorisation}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: '2px solid',
                  borderColor: age === o.key ? '#EAB308' : '#D1D5DB',
                  background: age === o.key ? '#EAB308' : '#fff',
                  color: age === o.key ? '#111' : '#374151',
                  fontWeight: age === o.key ? 700 : 500,
                  cursor: motorisation ? 'pointer' : 'not-allowed',
                  fontSize: '0.95rem', transition: 'all 0.15s'
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div style={{ color: '#B91C1C', background: '#FEE2E2', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: '0.875rem' }}>
            {error}
          </div>
        )}

        {/* Bouton estimer */}
        <button
          onClick={handleEstimer}
          style={{
            width: '100%', padding: '14px 0', borderRadius: 10,
            background: '#EAB308', border: 'none', color: '#111',
            fontWeight: 800, fontSize: '1.1rem', cursor: 'pointer',
            marginBottom: 28, transition: 'background 0.15s'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#CA8A04'}
          onMouseOut={e => e.currentTarget.style.background = '#EAB308'}
        >
          Estimer le coût
        </button>

        {/* Résultat */}
        {result && (() => {
          const montantDA = result.row[result.ageKey];
          const montantEUR = Math.round(montantDA / 156);
          const montantUSD = Math.round(montantDA / 133);
          const [cyl, energie] = motorisation.split('|');
          return (
            <div style={{
              background: '#111', borderRadius: 14, padding: '28px 24px',
              color: '#fff', border: '2px solid #EAB308', marginBottom: 32
            }}>
              <div style={{ fontSize: '0.8rem', color: '#EAB308', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Résultat de l'estimation
              </div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 4 }}>
                {marque} {modele}
              </h2>
              <p style={{ color: '#9CA3AF', fontSize: '0.875rem', marginBottom: 20 }}>
                {cyl} — {energie} — {ageLabel}
              </p>

              <div style={{ marginBottom: 20 }}>
                <div style={{ color: '#9CA3AF', fontSize: '0.8rem', marginBottom: 4 }}>Coût de dédouanement estimé</div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#EAB308' }}>
                  {formatNum(montantDA)} DA
                </div>
              </div>

              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 }}>
                <div style={{ background: '#1F2937', borderRadius: 8, padding: '12px 20px', flex: 1, minWidth: 140 }}>
                  <div style={{ color: '#9CA3AF', fontSize: '0.75rem', marginBottom: 2 }}>Equivalent EUR</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>≈ {formatNum(montantEUR)} €</div>
                </div>
                <div style={{ background: '#1F2937', borderRadius: 8, padding: '12px 20px', flex: 1, minWidth: 140 }}>
                  <div style={{ color: '#9CA3AF', fontSize: '0.75rem', marginBottom: 2 }}>Equivalent USD</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>≈ {formatNum(montantUSD)} $</div>
                </div>
              </div>

              <p style={{ color: '#6B7280', fontSize: '0.75rem', marginBottom: 20 }}>
                Taux utilisés : 1 EUR = 156 DZD / 1 USD = 133 DZD — Source : Barème DGD Algérie 2026
              </p>

              <Link
                to="/vehicles"
                style={{
                  display: 'inline-block', background: '#EAB308', color: '#111',
                  padding: '12px 24px', borderRadius: 8, fontWeight: 700,
                  textDecoration: 'none', fontSize: '0.95rem'
                }}
              >
                Voir nos véhicules disponibles
              </Link>
            </div>
          );
        })()}
      </main>
      <Footer />
    </div>
  );
}

const stepCard = {
  background: '#F9FAFB', border: '1px solid #E5E7EB',
  borderRadius: 12, padding: '20px 20px', marginBottom: 16
};

const stepHeader = {
  display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14
};

const stepBadge = {
  background: '#EAB308', color: '#111', borderRadius: '50%',
  width: 28, height: 28, display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem',
  flexShrink: 0
};

const stepTitle = {
  fontWeight: 700, fontSize: '1rem', color: '#111'
};

const selectStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: '1.5px solid #D1D5DB', fontSize: '0.95rem',
  color: '#111', background: '#fff', outline: 'none', cursor: 'pointer'
};
